export const queryKeys = {
  // Auth
  user: ['user'] as const,
  team: ['team'] as const,
  roles: ['roles'] as const,

  // WABA
  wabaList: ['waba-list'] as const,
  webhookHealth: (wabaId: string) => ['webhook-health', wabaId] as const,

  // Dashboard — optional params (e.g. {from,to}) are appended so the
  // query re-fetches when the date range changes. Callers that pass no
  // params get the legacy 2-element key.
  dashboard: (wabaId: string, params?: Record<string, unknown>) =>
    params === undefined
      ? (['dashboard', wabaId] as const)
      : (['dashboard', wabaId, params] as const),
  /** Redesigned dashboard: campaigns, AI, KB, inbox, CSAT, flows, spend. */
  dashboardSummary: (wabaId: string, params?: Record<string, unknown>) =>
    ['dashboard-summary', wabaId, params] as const,

  // Conversations / Inbox (legacy)
  conversations: (wabaId: string, filters?: Record<string, unknown>) =>
    ['conversations', wabaId, filters] as const,
  conversationMessages: (wabaId: string, conversationId: string) =>
    ['conversations', wabaId, conversationId, 'messages'] as const,

  // Inbox (new — contact-centric)
  inboxContacts: (wabaId: string, filters?: Record<string, unknown>) =>
    ['inbox-contacts', wabaId, filters] as const,
  inboxMessages: (wabaId: string, customerId: number, page?: number) =>
    ['inbox-messages', wabaId, customerId, page] as const,
  inboxCustomerDetail: (wabaId: string, customerId: number) =>
    ['inbox-customer-detail', wabaId, customerId] as const,
  /** Hand-over state of one chat (who is handling it). */
  inboxState: (wabaId: string, customerId: number) =>
    ['inbox-state', wabaId, customerId] as const,
  /** Team members a chat can be transferred to. */
  inboxTeam: (wabaId: string) => ['inbox-team', wabaId] as const,
  /** Meta call permission for one contact (fetched when the call menu opens). */
  inboxCallPermission: (wabaId: string, customerId: number) =>
    ['inbox-call-permission', wabaId, customerId] as const,
  /** Call history of one WABA phone (Inbox → Calls). */
  inboxCalls: (wabaId: string, filter: string) => ['inbox-calls', wabaId, filter] as const,
  /** Whether calling is on for one WABA phone (inbox call button). */
  callingStatus: (wabaId: string) => ['calling-status', wabaId] as const,
  /** Calling settings (Meta) of one WABA phone. */
  callingSettings: (wabaId: string) => ['calling-settings', wabaId] as const,

  // Customers
  customers: (wabaId: string, filters?: Record<string, unknown>) =>
    ['customers', wabaId, filters] as const,
  customer: (wabaId: string, customerId: string) =>
    ['customers', wabaId, customerId] as const,
  customerTags: (wabaId: string) => ['customer-tags', wabaId] as const,
  customerAttributes: (wabaId: string) => ['customer-attributes', wabaId] as const,

  // Segments
  segments: (wabaId: string) => ['customer-segments', wabaId] as const,
  segment: (wabaId: string, segmentId: string) =>
    ['customer-segments', wabaId, segmentId] as const,
  segmentPreview: (wabaId: string | number, segmentId: number, page?: number, pageSize?: number) =>
    ['customer-segments', wabaId, segmentId, 'preview', page, pageSize] as const,

  // Templates
  templateDrafts: (wabaId: string) => ['template-drafts', wabaId] as const,
  templates: (wabaId: string) => ['templates', wabaId] as const,
  template: (wabaId: string, templateId: string) =>
    ['templates', wabaId, templateId] as const,

  // Secret keys (named secrets injected into http_request headers)
  secretKeys: (wabaId: string) => ['secret-keys', wabaId] as const,
  secretKeyOptions: (wabaId: string) =>
    ['secret-key-options', wabaId] as const,

  // Setu Media library — prefix key invalidates every filter/search page.
  mediaLibraryAll: (wabaId: string) => ['media-library', wabaId] as const,
  mediaLibrary: (wabaId: string, filters: { kind: string; q: string }) =>
    ['media-library', wabaId, filters] as const,

  // Document templates (dynamic PDF templates)
  documentTemplates: (wabaId: string) => ['document-templates', wabaId] as const,
  documentTemplate: (wabaId: string, id: string | number) =>
    ['document-templates', wabaId, String(id)] as const,

  // Flows
  flows: (wabaId: string) => ['flows', wabaId] as const,
  flow: (wabaId: string, flowId: string) => ['flows', wabaId, flowId] as const,
  flowGraph: (wabaId: string, flowId: string) =>
    ['flow-graph', wabaId, flowId] as const,
  flowNodeTypes: (wabaId: string) => ['flow-node-types', wabaId] as const,
  flowTemplates: (wabaId: string) => ['flow-templates', wabaId] as const,
  flowSessions: (wabaId: string) => ['flow-sessions', wabaId] as const,
  flowLogs: (wabaId: string) => ['flow-logs', wabaId] as const,

  // Triggers
  triggers: (wabaId: string) => ['triggers', wabaId] as const,

  // WhatsApp Flows (Meta interactive forms)
  waFlows: (wabaId: string) => ['wa-flows', wabaId] as const,
  waFlowDetail: (wabaId: string, flowId: string) =>
    ['wa-flows', wabaId, flowId] as const,
  waFlowAnalytics: (wabaId: string, flowId: string, days?: number | 'all') =>
    ['wa-flows', wabaId, flowId, 'analytics', days] as const,
  waFlowResponses: (wabaId: string, flowId: string, page?: number, days?: number | 'all', pageSize?: number) =>
    ['wa-flows', wabaId, flowId, 'responses', page, days, pageSize] as const,

  // Campaigns
  campaigns: (wabaId: string, type?: string) =>
    ['campaigns', wabaId, type] as const,
  campaign: (wabaId: string, campaignId: string) =>
    ['campaigns', wabaId, campaignId] as const,
  campaignMetrics: (wabaId: string, campaignId: string) =>
    ['campaign-metrics', wabaId, campaignId] as const,

  // Analytics
  analytics: (wabaId: string, type: string, params?: Record<string, unknown>) =>
    ['analytics', wabaId, type, params] as const,

  // Knowledge Base
  knowledgeBases: (wabaId: string) => ['knowledge-bases', wabaId] as const,
  knowledgeBase: (wabaId: string, kbId: string) =>
    ['knowledge-bases', wabaId, kbId] as const,
  knowledgeDocuments: (wabaId: string, kbId?: string) =>
    ['knowledge-documents', wabaId, kbId] as const,

  // AI assistant (gatekeeper) + KB unanswered questions
  aiAssistantSettings: (wabaId: string) => ['ai-assistant', wabaId, 'settings'] as const,
  aiAssistantUsage: (wabaId: string) => ['ai-assistant', wabaId, 'usage'] as const,
  /** Prefix for every period/page of the OpenAI usage log. */
  aiAssistantUsageLogAll: (wabaId: string) => ['ai-assistant', wabaId, 'usage-log'] as const,
  aiAssistantUsageLog: (wabaId: string, period: string, page: number) =>
    ['ai-assistant', wabaId, 'usage-log', period, page] as const,
  kbUnanswered: (wabaId: string) => ['kb-unanswered', wabaId] as const,

  // AI Chatbot
  chatbotIntents: (wabaId: string, params?: Record<string, unknown>) =>
    ['chatbot-intents', wabaId, params] as const,
  chatbotEntities: (wabaId: string, params?: Record<string, unknown>) =>
    ['chatbot-entities', wabaId, params] as const,
  chatbotFlows: (wabaId: string, params?: Record<string, unknown>) =>
    ['chatbot-flows', wabaId, params] as const,
  chatbotTrainingData: (wabaId: string, params?: Record<string, unknown>) =>
    ['chatbot-training-data', wabaId, params] as const,
  chatbotDashboard: (wabaId: string) => ['chatbot-dashboard', wabaId] as const,
  chatbotTypes: (wabaId: string, resource: string) =>
    ['chatbot-types', wabaId, resource] as const,
  chatbotSalesConfig: (wabaId: string) => ['chatbot-sales-config', wabaId] as const,
  chatbotConversionInsights: (wabaId: string) =>
    ['chatbot-conversion-insights', wabaId] as const,
  chatbotLlmHealth: (wabaId: string) => ['chatbot-llm-health', wabaId] as const,

  // Billing
  billingBalance: (wabaId: string) => ['billing-balance', wabaId] as const,
  billingUsage: (wabaId: string) => ['billing-usage', wabaId] as const,
  billingInvoices: (wabaId: string) => ['billing-invoices', wabaId] as const,
  billingPricing: (wabaId: string) => ['billing-pricing', wabaId] as const,
  billingPlans: (wabaId: string) => ['billing-plans', wabaId] as const,
  billingCreditPacks: (wabaId: string) => ['billing-credit-packs', wabaId] as const,
  billingSubscription: (wabaId: string) => ['billing-subscription', wabaId] as const,
  billingTransactions: (wabaId: string) => ['billing-transactions', wabaId] as const,

  // Settings
  apiKeys: ['api-keys'] as const,
  company: (companyId: string) => ['company', companyId] as const,
  keywordRules: (wabaId: string) => ['keyword-rules', wabaId] as const,
  chatPayments: (wabaId: string, conversationId: string) =>
    ['chat-payments', wabaId, conversationId] as const,

  // Integrations
  integrationProviders: ['integration-providers'] as const,
  integrationConfigs: ['integration-configs'] as const,

  // Webhooks (subscription management)
  webhookSubscriptions: ['webhook-subscriptions'] as const,
  webhookDeliveries: (webhookId: number) => ['webhook-deliveries', webhookId] as const,

  // E-commerce
  ecommerceStores: (wabaId: string) => ['ecommerce-stores', wabaId] as const,
  ecommerceProducts: (wabaId: string) => ['ecommerce-products', wabaId] as const,
  ecommerceOrders: (wabaId: string, params?: Record<string, unknown>) =>
    params === undefined
      ? (['ecommerce-orders', wabaId] as const)
      : (['ecommerce-orders', wabaId, params] as const),

  // Store integrations (Shopify app …) — company-scoped, no WABA id.
  // Every key starts with 'store-integrations', so `storeIntegrations.all`
  // invalidates the lot; a connection's sub-keys share its prefix.
  storeIntegrations: {
    all: ['store-integrations'] as const,
    providers: ['store-integrations', 'providers'] as const,
    connections: (params?: Record<string, unknown>) =>
      params === undefined
        ? (['store-integrations', 'connections'] as const)
        : (['store-integrations', 'connections', params] as const),
    connection: (id: number) => ['store-integrations', 'connection', id] as const,
    mappings: (id: number) => ['store-integrations', 'connection', id, 'mappings'] as const,
    deliveries: (id: number, params?: Record<string, unknown>) =>
      params === undefined
        ? (['store-integrations', 'connection', id, 'deliveries'] as const)
        : (['store-integrations', 'connection', id, 'deliveries', params] as const),
    complianceRequests: (id: number) =>
      ['store-integrations', 'connection', id, 'compliance-requests'] as const,
  },

  // Notifications
  notifications: (params?: Record<string, unknown>) =>
    ['notifications', params] as const,
  notificationUnreadCount: ['notification-unread-count'] as const,
  notificationPreferences: ['notification-preferences'] as const,

  // Pipeline
  pipeline: {
    all: (wabaId: string | null) => ['pipeline', wabaId] as const,
    pipelines: (wabaId: string | null) => ['pipeline', wabaId, 'list'] as const,
    deals: (wabaId: string | null, params?: Record<string, unknown>) =>
      ['pipeline', wabaId, 'deals', params] as const,
    deal: (wabaId: string | null, dealId: number) =>
      ['pipeline', wabaId, 'deals', dealId] as const,
  },

  // Import/Export
  importExport: (wabaId: string, params?: Record<string, unknown>) =>
    ['import-export', wabaId, params] as const,
  importExportJob: (wabaId: string, jobId: number) =>
    ['import-export', wabaId, jobId] as const,

  // Agent assist (P1 Item 21 — suggest reply + summarize thread)
  agentAssist: {
    suggestReply: (wabaId: string, source: string, identifier: string) =>
      ['agent-assist', wabaId, 'suggest-reply', source, identifier] as const,
    summarize: (wabaId: string, source: string, identifier: string) =>
      ['agent-assist', wabaId, 'summarize', source, identifier] as const,
  },

  // CSAT dashboard + CX-score-lite (P1 Item 22)
  csat: {
    dashboard: (wabaId: string, days?: number) =>
      ['csat', wabaId, 'dashboard', days ?? 'all'] as const,
    surveyAnalytics: (wabaId: string, surveyId: number, days?: number) =>
      ['csat', wabaId, 'analytics', surveyId, days ?? 'all'] as const,
    surveys: (wabaId: string) => ['csat', wabaId, 'surveys'] as const,
    followUps: (wabaId: string) => ['csat', wabaId, 'follow-ups'] as const,
    responses: (wabaId: string, params: Record<string, unknown>) =>
      ['csat', wabaId, 'responses', params] as const,
  },

  // CTWA (Click-to-WhatsApp ads) analytics (P2 Item 24)
  ctwa: {
    roi: (wabaId: string, params?: Record<string, unknown>) =>
      ['ctwa', wabaId, 'roi', params] as const,
    sources: (wabaId: string, params?: Record<string, unknown>) =>
      ['ctwa', wabaId, 'sources', params] as const,
  },

  // Message translation (P2 Item 26 — pluggable translation backend)
  translation: (wabaId: string, messageId: string, target: string) =>
    ['translation', wabaId, messageId, target] as const,

  // API Activity — what was created/triggered through X-Api-Key
  apiActivity: (params?: Record<string, unknown>) =>
    ['api-activity', params] as const,
  apiActivityStats: ['api-activity', 'stats'] as const,
} as const
